/// <reference types="vite/client" />
import styles from './marketing-overview.css?inline';
import { loadBootstrapConfig, fetchModels, resolveImageUrl } from '../api/client';
import type { ModelSummary, BootstrapConfig } from '../api/types';

(function () {
  let script = document.currentScript as HTMLScriptElement | null;
  if (!script) {
    // Dev module-mode fallback: this file is loaded as type="module" by Vite.
    script = Array.from(document.querySelectorAll<HTMLScriptElement>('script[type="module"]'))
      .find(s => s.src.includes('marketing-overview')) as HTMLScriptElement | undefined ?? null;
  }
  if (!script) return;

  const apiUrl = (script.dataset.apiUrl ?? '').replace(/\/+$/, '');
  if (apiUrl) {
    (window as any).__VC_API_URL__ = apiUrl;
  }

  const containerId = script.dataset.container || '#vc-model-overview';
  let container = document.querySelector(containerId) as HTMLElement | null;
  if (!container) {
    container = document.createElement('div');
    container.id = containerId.replace(/^[#.]/, '');
    script.parentElement?.insertBefore(container, script);
  }

  const explicitTenantId = (script.dataset.tenantId ?? '').trim();

  // Clear any stale tenant id, then optionally set an explicit one for dev/test pages.
  localStorage.removeItem('vc_tenant_id');
  if (explicitTenantId && /^\d+$/.test(explicitTenantId)) {
    localStorage.setItem('vc_tenant_id', explicitTenantId);
  }

  // Inject styles
  const styleEl = document.createElement('style');
  styleEl.textContent = styles;
  document.head.appendChild(styleEl);

  const isSSR = container.hasAttribute('data-vc-ssr');
  container.classList.add('vc-marketing-overview');
  if (!isSSR) {
    container.innerHTML = '<p class="vc-marketing-empty">Loading models...</p>';
  }

  async function init(): Promise<void> {
    try {
      const [config, models] = await Promise.all([
        loadBootstrapConfig(),
        fetchModels(),
      ]);

      if (!isSSR) {
        applyTheme(container!, config);
      }
      render(container!, models, config);
    } catch (err) {
      container!.innerHTML = '<p class="vc-marketing-empty">Unable to load models. Please try again later.</p>';
    }
  }

  function applyTheme(el: HTMLElement, config: BootstrapConfig): void {
    const s = config.settings ?? {};
    const hex = (key: string, fallback: string): string =>
      typeof s[key] === 'string' && /^#[0-9a-fA-F]{6}$/.test(s[key] as string)
        ? (s[key] as string)
        : fallback;

    el.style.setProperty('--vc-primary', hex('themeColorPrimary', '#0e8e92'));
    el.style.setProperty('--vc-primary-hover', hex('themeColorPrimaryHover', '#1557b0'));
    el.style.setProperty('--vc-primary-light', hex('themeColorPrimaryLight', '#e8f0fe'));
    el.style.setProperty('--vc-text', hex('themeColorText', '#202124'));
    el.style.setProperty('--vc-bg', hex('themeColorBg', '#ffffff'));
    el.style.setProperty('--vc-border-solid', hex('themeColorBorder', '#0e8e92'));
    el.style.setProperty('--vc-border', hex('themeColorBorder', '#0e8e92') + '55');
  }

  function getDetailsUrl(template: string | undefined, model: ModelSummary): string {
    if (!template || !model.url_slug) return '#';
    return template
      .replace(/\{vessel-url-slug\}/g, model.url_slug)
      .replace(/\{slug\}/g, model.url_slug)
      .replace(/\{id\}/g, String(model.id))
      .replace(/\{model_code\}/g, encodeURIComponent(model.model_code));
  }

  function getCategoryDescriptions(settings: Record<string, unknown> | undefined): Record<string, string> {
    const value = settings?.marketingCategoryDescriptions;
    let parsed: unknown = value;
    if (typeof value === 'string') {
      try { parsed = JSON.parse(value); } catch { parsed = {}; }
    }
    if (!parsed || typeof parsed !== 'object') return {};
    const result: Record<string, string> = {};
    for (const [key, val] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof val === 'string') result[key] = val;
    }
    return result;
  }

  function render(el: HTMLElement, models: ModelSummary[], config: BootstrapConfig): void {
    const template =
      (script?.dataset.detailsUrlTemplate as string | undefined) ||
      (config.settings?.modelDetailsUrlTemplate as string | undefined);

    const s = config.settings ?? {};
    const showTitle = s.marketingOverviewShowTitle !== false;
    const showSearch = s.marketingOverviewShowSearch !== false;
    const titleText =
      typeof s.marketingOverviewTitle === 'string' && s.marketingOverviewTitle.trim() !== ''
        ? s.marketingOverviewTitle
        : 'Choose Your Model';

    const ssr = el.hasAttribute('data-vc-ssr');
    if (!ssr) {
      el.innerHTML = `
        ${showTitle ? `<h2>${escapeHtml(titleText)}</h2>` : ''}
        ${showSearch ? `<div class="vc-marketing-overview-search"><input type="text" id="vc-overview-search" placeholder="Search models..." /></div>` : ''}
        <div id="vc-overview-grid"></div>
      `;
    }

    const grid = el.querySelector('#vc-overview-grid') as HTMLElement;
    const searchInput = el.querySelector('#vc-overview-search') as HTMLInputElement | null;

    const categoryDescriptions = getCategoryDescriptions(config.settings);

    function renderGrid(items: ModelSummary[]): void {
      if (items.length === 0) {
        grid.innerHTML = '<p class="vc-marketing-empty">No models found.</p>';
        return;
      }

      const groups = new Map<string, ModelSummary[]>();
      for (const m of items) {
        const cat = m.category || 'Other';
        if (!groups.has(cat)) groups.set(cat, []);
        groups.get(cat)!.push(m);
      }

      let html = '';
      for (const [category, catModels] of groups) {
        const catDesc = categoryDescriptions[category];
        html += `<div class="vc-marketing-category">`;
        html += `<h3>${escapeHtml(category)}</h3>`;
        if (catDesc) {
          html += `<p class="vc-marketing-category-description">${escapeHtml(catDesc)}</p>`;
        }
        html += `<div class="vc-marketing-grid">`;
        html += catModels.map(m => {
          const href = getDetailsUrl(template, m);
          const length = m.length ? `${m.length}'` : '';
          return `
            <a class="vc-marketing-card" href="${href}">
              <div class="vc-marketing-card-thumb">
                <img src="${resolveImageUrl(m.thumbnail)}" alt="${escapeHtml(m.model_name)}" loading="lazy" />
              </div>
              <div class="vc-marketing-card-info">
                <div class="vc-marketing-card-content">
                  <h4>${escapeHtml(m.model_name)}</h4>
                  <div class="vc-marketing-card-meta">
                    <span>${m.model_year}</span>
                    ${length ? `<span>${length}</span>` : ''}
                  </div>
                  <span class="vc-marketing-card-category">${escapeHtml(category)}</span>
                </div>
                <span class="vc-marketing-card-cta">Explore</span>
              </div>
            </a>
          `;
        }).join('');
        html += `</div></div>`;
      }
      grid.innerHTML = html;
    }

    if (!ssr) {
      renderGrid(models);
    }

    if (searchInput) {
      searchInput.addEventListener('input', () => {
        const q = searchInput.value.toLowerCase();
        const filtered = models.filter(m =>
          !q ||
          m.model_name.toLowerCase().includes(q) ||
          (m.category ?? '').toLowerCase().includes(q) ||
          (m.model_code ?? '').toLowerCase().includes(q)
        );
        renderGrid(filtered);
      });
    }
  }

  function escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  void init();
})();
